import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ContentCopy as ContentCopyIcon,
  LockOpen as LockOpenIcon,
  RestartAlt as ResetIcon,
  Refresh as RefreshIcon,
  Schedule as ScheduleIcon,
  Search as SearchIcon,
  Shield as ShieldIcon,
  ShieldOutlined as ShieldOffIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';

import { PermissionsEnum } from '@/auth/interfaces';
import { hasPermission } from '@/shared/utils';
import {
  MfaEstadoLista,
  MfaUsuarioRow,
  useAsignarGrace,
  useDesbloquearMfa,
  useForzarMfa,
  useMfaListado,
  useRegenerarBackupAdmin,
  useResetearMfa,
} from '@/shared/actions/erp/administration/mfa-admin';

const ESTADOS: { value: MfaEstadoLista; label: string }[] = [
  { value: 'todos', label: 'Todos' },
  { value: 'con', label: 'Con 2FA' },
  { value: 'sin', label: 'Sin 2FA' },
  { value: 'bloqueado', label: 'Bloqueados' },
  { value: 'forzados', label: 'Forzados' },
];

export const MfaAdminUsuariosTab = () => {
  const puedeAdmin = hasPermission(PermissionsEnum.api_administrar_mfa);
  const puedeResetear = hasPermission(PermissionsEnum.api_resetear_mfa_usuarios);

  const [q, setQ] = useState('');
  const [qInput, setQInput] = useState('');
  const [estado, setEstado] = useState<MfaEstadoLista>('todos');
  const [metodo, setMetodo] = useState<'' | 'app' | 'email'>('');
  const [page, setPage] = useState(1);
  const pageSize = 25;

  const { data, isLoading, isFetching } = useMfaListado({
    q,
    estado: estado === 'todos' ? undefined : estado,
    metodo: metodo || undefined,
    page,
    page_size: pageSize,
  });

  const resetear = useResetearMfa();
  const desbloquear = useDesbloquearMfa();
  const regenerar = useRegenerarBackupAdmin();
  const forzar = useForzarMfa();
  const grace = useAsignarGrace();

  const [resetTarget, setResetTarget] = useState<MfaUsuarioRow | null>(null);
  const [resetMotivo, setResetMotivo] = useState('');
  const [graceTarget, setGraceTarget] = useState<MfaUsuarioRow | null>(null);
  const [graceDias, setGraceDias] = useState<number>(7);
  const [graceLimpiar, setGraceLimpiar] = useState<boolean>(false);
  const [forzarTarget, setForzarTarget] = useState<MfaUsuarioRow | null>(null);
  const [forzarDias, setForzarDias] = useState<number>(7);

  const diasRestantesDeGrace = (graceUntil: string | null): number => {
    if (!graceUntil) return 0;
    const ms = new Date(graceUntil).getTime() - Date.now();
    if (!isFinite(ms) || ms <= 0) return 0;
    return Math.ceil(ms / (24 * 60 * 60 * 1000));
  };

  const abrirDialogoGrace = (u: MfaUsuarioRow) => {
    const restantes = diasRestantesDeGrace(u.mfa_grace_until);
    setGraceDias(restantes > 0 ? restantes : 7);
    setGraceLimpiar(false);
    setGraceTarget(u);
  };
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [backupOpen, setBackupOpen] = useState(false);

  const filas = data?.data ?? [];
  const total = data?.count ?? 0;
  const numPaginas = data?.numero_paginas ?? 1;

  const aplicarBusqueda = () => {
    setPage(1);
    setQ(qInput.trim());
  };

  const handleReset = async () => {
    if (!resetTarget) return;
    try {
      await resetear.mutateAsync({ userId: resetTarget.id, motivo: resetMotivo });
      toast.success(`2FA reseteado para ${resetTarget.username}`);
      setResetTarget(null);
      setResetMotivo('');
    } catch {
      /* interceptor toastea */
    }
  };

  const handleDesbloquear = async (u: MfaUsuarioRow) => {
    try {
      await desbloquear.mutateAsync(u.id);
      toast.success(`Intentos reseteados para ${u.username}`);
    } catch {
      /* noop */
    }
  };

  const handleRegenerar = async (u: MfaUsuarioRow) => {
    try {
      const res = await regenerar.mutateAsync(u.id);
      setBackupCodes(res.backup_codes);
      setBackupOpen(true);
      toast.success(`Backup codes regenerados para ${u.username}`);
    } catch {
      /* noop */
    }
  };

  const handleForzar = async () => {
    if (!forzarTarget) return;
    try {
      await forzar.mutateAsync({
        userId: forzarTarget.id,
        obligatorio: true,
        diasGrace: forzarDias,
      });
      toast.success(`2FA marcado obligatorio para ${forzarTarget.username}`);
      setForzarTarget(null);
    } catch {
      /* noop */
    }
  };

  const handleQuitarForzar = async (u: MfaUsuarioRow) => {
    try {
      await forzar.mutateAsync({ userId: u.id, obligatorio: false });
      toast.success(`Obligatoriedad retirada para ${u.username}`);
    } catch {
      /* noop */
    }
  };

  const handleGrace = async () => {
    if (!graceTarget) return;
    try {
      if (graceLimpiar) {
        await grace.mutateAsync({ userId: graceTarget.id, limpiar: true });
        toast.success(`Período de gracia eliminado para ${graceTarget.username}`);
      } else {
        if (graceDias <= 0) {
          toast.error('Ingresa una cantidad de días mayor a cero');
          return;
        }
        await grace.mutateAsync({ userId: graceTarget.id, dias: graceDias });
        toast.success(`Período de gracia actualizado para ${graceTarget.username}`);
      }
      setGraceTarget(null);
    } catch {
      /* noop */
    }
  };

  const handleVencerAhora = async () => {
    if (!graceTarget) return;
    try {
      await grace.mutateAsync({
        userId: graceTarget.id,
        vencer_ahora: true,
      });
      toast.success(
        `Bloqueo activado para ${graceTarget.username}. ` +
        `Su próximo login será rechazado hasta que configure 2FA.`,
      );
      setGraceTarget(null);
    } catch {
      /* noop */
    }
  };

  const copiarBackup = () => {
    navigator.clipboard.writeText(backupCodes.join('\n'));
    toast.success('Códigos copiados');
  };

  const noPermiso = useMemo(() => !puedeAdmin, [puedeAdmin]);

  if (noPermiso) {
    return (
      <Alert severity="warning">
        No tienes permisos para administrar MFA. Solicita el permiso{' '}
        <code>api.administrar_mfa</code>.
      </Alert>
    );
  }

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              size="small"
              fullWidth
              label="Buscar (usuario / email / nombre)"
              value={qInput}
              onChange={(e) => setQInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && aplicarBusqueda()}
              InputProps={{
                endAdornment: (
                  <IconButton size="small" onClick={aplicarBusqueda}>
                    <SearchIcon fontSize="small" />
                  </IconButton>
                ),
              }}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <FormControl size="small" fullWidth>
              <InputLabel>Estado</InputLabel>
              <Select
                label="Estado"
                value={estado}
                onChange={(e) => {
                  setEstado(e.target.value as MfaEstadoLista);
                  setPage(1);
                }}
              >
                {ESTADOS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={3}>
            <FormControl size="small" fullWidth>
              <InputLabel>Método</InputLabel>
              <Select
                label="Método"
                value={metodo}
                onChange={(e) => {
                  setMetodo(e.target.value as '' | 'app' | 'email');
                  setPage(1);
                }}
              >
                <MenuItem value="">Cualquiera</MenuItem>
                <MenuItem value="app">App</MenuItem>
                <MenuItem value="email">Email</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2} sx={{ textAlign: 'right' }}>
            <Typography variant="caption" color="text.secondary">
              {total} usuario(s)
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Usuario</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Estado 2FA</TableCell>
              <TableCell>Método</TableCell>
              <TableCell align="center">Intentos</TableCell>
              <TableCell align="center">Backup</TableCell>
              <TableCell>Obligatorio</TableCell>
              <TableCell>Vence gracia</TableCell>
              <TableCell>Último uso</TableCell>
              <TableCell align="right" sx={{ minWidth: 320 }}>
                Acciones
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading || isFetching ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : filas.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <Typography variant="body2" color="text.secondary">
                    Sin resultados
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filas.map((u) => (
                <TableRow key={u.id} hover>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2">{u.username}</Typography>
                      {u.es_usuario_integracion && (
                        <Chip size="small" label="API" color="info" />
                      )}
                      {u.is_superuser && (
                        <Chip size="small" label="super" color="warning" />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {u.email || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={u.mfa_habilitado ? 'Activo' : 'Inactivo'}
                      color={u.mfa_habilitado ? 'success' : 'default'}
                      variant={u.mfa_habilitado ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell>
                    {u.mfa_metodo === 'app'
                      ? 'App'
                      : u.mfa_metodo === 'email'
                      ? 'Email'
                      : '-'}
                  </TableCell>
                  <TableCell align="center">
                    {u.mfa_bloqueado ? (
                      <Chip size="small" label={`${u.mfa_intentos} (bloqueado)`} color="error" />
                    ) : (
                      u.mfa_intentos
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {u.tiene_backup_codes ? u.cantidad_backup_codes : '-'}
                  </TableCell>
                  <TableCell>
                    {u.mfa_obligatorio ? (
                      <Chip size="small" label="Forzado" color="warning" />
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        no
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {u.mfa_grace_until ? (
                      <Tooltip
                        title={new Date(u.mfa_grace_until).toLocaleString()}
                      >
                        <Chip
                          size="small"
                          label={`${diasRestantesDeGrace(u.mfa_grace_until)} día(s)`}
                          color={
                            diasRestantesDeGrace(u.mfa_grace_until) <= 1
                              ? 'error'
                              : diasRestantesDeGrace(u.mfa_grace_until) <= 3
                              ? 'warning'
                              : 'default'
                          }
                          variant="outlined"
                        />
                      </Tooltip>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        -
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {u.mfa_ultimo_uso
                        ? new Date(u.mfa_ultimo_uso).toLocaleString()
                        : '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Stack
                      direction="row"
                      spacing={0.5}
                      justifyContent="flex-end"
                      flexWrap="wrap"
                      rowGap={0.5}
                    >
                      <Tooltip title="Resetear 2FA (apagar y limpiar)">
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            disabled={
                              !puedeResetear ||
                              u.es_usuario_integracion ||
                              !u.mfa_habilitado
                            }
                            onClick={() => setResetTarget(u)}
                          >
                            <ResetIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Desbloquear intentos">
                        <span>
                          <IconButton
                            size="small"
                            disabled={!puedeResetear || !u.mfa_bloqueado}
                            onClick={() => handleDesbloquear(u)}
                          >
                            <LockOpenIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Regenerar backup codes">
                        <span>
                          <IconButton
                            size="small"
                            disabled={!puedeResetear || !u.mfa_habilitado}
                            onClick={() => handleRegenerar(u)}
                          >
                            <RefreshIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Asignar / extender período de gracia">
                        <span>
                          <IconButton
                            size="small"
                            disabled={!puedeResetear}
                            onClick={() => abrirDialogoGrace(u)}
                          >
                            <ScheduleIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      {u.mfa_obligatorio ? (
                        <Tooltip title="Quitar obligatoriedad">
                          <span>
                            <IconButton
                              size="small"
                              color="warning"
                              disabled={!puedeResetear}
                              onClick={() => handleQuitarForzar(u)}
                            >
                              <ShieldOffIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Forzar 2FA obligatorio">
                          <span>
                            <IconButton
                              size="small"
                              color="primary"
                              disabled={!puedeResetear || u.es_usuario_integracion}
                              onClick={() => {
                                setForzarTarget(u);
                                setForzarDias(7);
                              }}
                            >
                              <ShieldIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box display="flex" justifyContent="flex-end">
        <Pagination
          count={numPaginas}
          page={page}
          onChange={(_, p) => setPage(p)}
          size="small"
        />
      </Box>

      {/* ---- Diálogo Reset ---- */}
      <Dialog
        open={!!resetTarget}
        onClose={() => setResetTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Resetear 2FA</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Esto desactiva el doble factor de <strong>{resetTarget?.username}</strong> y
            elimina su secreto, códigos de respaldo e intentos. El usuario podrá
            iniciar sesión solo con su contraseña hasta que vuelva a configurarlo.
          </Typography>
          <TextField
            label="Motivo (opcional, queda en bitácora)"
            fullWidth
            size="small"
            value={resetMotivo}
            onChange={(e) => setResetMotivo(e.target.value)}
            inputProps={{ maxLength: 200 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetTarget(null)}>Cancelar</Button>
          <Button
            color="error"
            variant="contained"
            disabled={resetear.isPending}
            onClick={handleReset}
          >
            Resetear
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---- Diálogo Forzar ---- */}
      <Dialog
        open={!!forzarTarget}
        onClose={() => setForzarTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Forzar 2FA obligatorio</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Se exigirá a <strong>{forzarTarget?.username}</strong> tener 2FA
            configurado. Si no lo tiene aún, se le concederá un período de
            gracia de N días para configurarlo.
          </Typography>
          <TextField
            label="Días del período de gracia"
            type="number"
            size="small"
            fullWidth
            value={forzarDias}
            onChange={(e) => setForzarDias(parseInt(e.target.value || '0', 10))}
            inputProps={{ min: 0, max: 365 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setForzarTarget(null)}>Cancelar</Button>
          <Button
            variant="contained"
            disabled={forzar.isPending}
            onClick={handleForzar}
          >
            Forzar
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---- Diálogo Período de Gracia ---- */}
      <Dialog
        open={!!graceTarget}
        onClose={() => setGraceTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Período de gracia</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Define cuántos días más se le permite a{' '}
            <strong>{graceTarget?.username}</strong> iniciar sesión sin tener
            2FA configurado.
          </Typography>
          {graceTarget?.mfa_grace_until ? (
            <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
              Vence actualmente el{' '}
              <strong>
                {new Date(graceTarget.mfa_grace_until).toLocaleString()}
              </strong>{' '}
              ({diasRestantesDeGrace(graceTarget.mfa_grace_until)} día(s)
              restantes).
            </Alert>
          ) : (
            <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
              El usuario no tiene período de gracia asignado.
            </Alert>
          )}
          <TextField
            label="Nuevos días"
            type="number"
            size="small"
            fullWidth
            value={graceDias}
            disabled={graceLimpiar}
            onChange={(e) =>
              setGraceDias(parseInt(e.target.value || '0', 10))
            }
            inputProps={{ min: 1, max: 365 }}
            helperText="La fecha de vencimiento se calcula desde el momento de guardar."
          />
          <Box mt={2}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Switch
                size="small"
                checked={graceLimpiar}
                onChange={(e) => setGraceLimpiar(e.target.checked)}
              />
              <Typography variant="caption">
                Eliminar período de gracia
              </Typography>
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', flexWrap: 'wrap', rowGap: 1 }}>
          <Button
            color="error"
            variant="text"
            disabled={grace.isPending}
            onClick={handleVencerAhora}
          >
            Bloquear ahora
          </Button>
          <Box>
            <Button onClick={() => setGraceTarget(null)} sx={{ mr: 1 }}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              disabled={grace.isPending}
              onClick={handleGrace}
            >
              Guardar
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* ---- Diálogo Backup codes (mostrar una sola vez) ---- */}
      <Dialog
        open={backupOpen}
        onClose={() => setBackupOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Códigos de respaldo</DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Entrega estos códigos al usuario por canal seguro. No volverán a
            mostrarse — los anteriores ya quedaron invalidados.
          </Alert>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: 1,
              fontFamily: 'monospace',
              bgcolor: 'action.hover',
              p: 2,
              borderRadius: 1,
            }}
          >
            {backupCodes.map((c) => (
              <Typography key={c} variant="body1">
                {c}
              </Typography>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={copiarBackup} startIcon={<ContentCopyIcon />}>
            Copiar todos
          </Button>
          <Button variant="contained" onClick={() => setBackupOpen(false)}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};
